import type { SocialProvider } from '../api/userland'

export const OAUTH_POPUP_NAME = 'wils-oauth'
const CHANNEL = 'wils-oauth'
const RESUME_KEY = 'wils.oauth.resume'
const REVEAL_KEY = 'wils.pendingReveal'
const REVEAL_READY_KEY = 'wils.pendingReveal.ready'

export type OAuthResume = {
  intent: 'login' | 'confirm'
  path: string
}

export type OAuthSignal = {
  ok: boolean
  provider: SocialProvider
  error?: string
  register?: boolean
  ticket?: string
}

export function isOAuthPopup(win: Window = window): boolean {
  return win.name === OAUTH_POPUP_NAME
}

/** Fallback when the OAuth popup is blocked. Prefer window.open (login and Confirm). */
export function assignLocation(url: string): void {
  window.location.assign(url)
}

/**
 * Confirm must show a real provider prompt even if the browser already has
 * an SSO session (Google otherwise closes the popup and the seed opens).
 * Login keeps the provider default.
 */
export function oauthAuthorizeURL(
  authUrl: string,
  provider: SocialProvider,
  intent: OAuthResume['intent']
): string {
  if (intent !== 'confirm') return authUrl
  try {
    const url = new URL(authUrl)
    url.searchParams.set('prompt', provider === 'google' ? 'select_account' : 'consent')
    url.searchParams.set('max_age', '0')
    return url.toString()
  } catch {
    return authUrl
  }
}

export function writeOAuthResume(intent: OAuthResume['intent'], path: string): void {
  sessionStorage.setItem(RESUME_KEY, JSON.stringify({ intent, path }))
}

/** The purpose of the OAuth under way, left in place for takeOAuthResume. */
export function peekOAuthResumeIntent(): OAuthResume['intent'] | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY)
    if (!raw) return null
    const intent = (JSON.parse(raw) as Partial<OAuthResume>).intent
    return intent === 'login' || intent === 'confirm' ? intent : null
  } catch {
    return null
  }
}

export function takeOAuthResume(): OAuthResume | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY)
    sessionStorage.removeItem(RESUME_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<OAuthResume>
    if (parsed.intent !== 'login' && parsed.intent !== 'confirm') return null
    if (typeof parsed.path !== 'string' || !parsed.path.startsWith('/')) return null
    return { intent: parsed.intent, path: parsed.path }
  } catch {
    return null
  }
}

export function writePendingReveal(walletId: string): void {
  sessionStorage.setItem(REVEAL_KEY, walletId)
  sessionStorage.removeItem(REVEAL_READY_KEY)
}

export function markPendingRevealConfirmed(): void {
  if (!sessionStorage.getItem(REVEAL_KEY)) return
  sessionStorage.setItem(REVEAL_READY_KEY, '1')
}

export function peekPendingReveal(): string | null {
  try {
    return sessionStorage.getItem(REVEAL_KEY)
  } catch {
    return null
  }
}

/** Full-page OAuth may restore reveal only after Confirm actually succeeded. */
export function peekPendingRevealConfirmed(): boolean {
  try {
    return Boolean(
      sessionStorage.getItem(REVEAL_KEY) && sessionStorage.getItem(REVEAL_READY_KEY) === '1'
    )
  } catch {
    return false
  }
}

export function clearPendingReveal(): void {
  sessionStorage.removeItem(REVEAL_KEY)
  sessionStorage.removeItem(REVEAL_READY_KEY)
}

export function subscribeOAuthSignal(handler: (msg: OAuthSignal) => void): () => void {
  let channel: BroadcastChannel | null = null
  const onChannel = (event: MessageEvent) => {
    if (isOAuthSignal(event.data)) handler(event.data)
  }
  try {
    channel = new BroadcastChannel(CHANNEL)
    channel.addEventListener('message', onChannel)
  } catch {
    channel = null
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key !== CHANNEL || !event.newValue) return
    try {
      const parsed: unknown = JSON.parse(event.newValue)
      if (isOAuthSignal(parsed)) handler(parsed)
    } catch {
      // ignore
    }
  }
  window.addEventListener('storage', onStorage)
  return () => {
    channel?.removeEventListener('message', onChannel)
    channel?.close()
    window.removeEventListener('storage', onStorage)
  }
}

export function publishOAuthSignal(msg: OAuthSignal): void {
  try {
    const channel = new BroadcastChannel(CHANNEL)
    channel.postMessage(msg)
    channel.close()
  } catch {
    // BroadcastChannel missing — storage ping below still wakes the opener.
  }
  try {
    localStorage.setItem(CHANNEL, JSON.stringify({ ...msg, t: Date.now() }))
    localStorage.removeItem(CHANNEL)
  } catch {
    // storage blocked
  }
}

function isOAuthSignal(value: unknown): value is OAuthSignal {
  if (!value || typeof value !== 'object') return false
  const row = value as Partial<OAuthSignal>
  if (typeof row.ok !== 'boolean') return false
  return row.provider === 'google' || row.provider === 'telegram'
}
